import { useEffect, useMemo, useRef } from 'react';

/**
 * Returns a throttled version of a callback limited to one call per timeFrameMs
 * milliseconds
 *
 * When the throttled function is called twice or more within the same time
 * frame, it will run `cb` immediately with the parameters given (leading edge
 * call) and will call `cb` with the parameters from the last invocation of the
 * throttled function after timeFrameMs milliseconds have passed (trailing edge
 * call).
 *
 * @param cb Callback to create throttled version of. It should be memoized
 * prior to being used in this function. If the function changes on every
 * render, every render will reset the internal state used to throttle, and it
 * will not work correctly.
 * @param timeFrameMs the duration of a single time frame in milliseconds
 * @returns a throttled version of `cb`
 */
export const useThrottled = <T extends unknown[]>(
  cb: (...args: T) => unknown,
  timeFrameMs: number,
) => {
  const trailingCallRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledCb = useMemo(() => {
    let lastTime = 0;
    return function (...args: T) {
      let now = Date.now();

      // Clear any remaining trailing edge calls of the function, as they may
      // contain stale parameters
      if (trailingCallRef.current !== null) {
        clearTimeout(trailingCallRef.current);
        trailingCallRef.current = null;
      }

      if (now - lastTime >= timeFrameMs) {
        // leading edge call
        cb(...args);
        lastTime = now;
      } else {
        // trailing edge call
        const timeToEndOfFrame = timeFrameMs - (now - lastTime);
        trailingCallRef.current = setTimeout(() => {
          cb(...args);
          lastTime = Date.now();
          trailingCallRef.current = null;
        }, timeToEndOfFrame);
      }
    };
  }, [cb, trailingCallRef, timeFrameMs]);

  useEffect(() => {
    // In the cleanup stage cancel any leftover trailing calls to avoid late
    // setState calls on unmounted components, etc.
    return () => {
      if (trailingCallRef.current !== null) {
        clearTimeout(trailingCallRef.current);
        trailingCallRef.current = null;
      }
    };
  }, [throttledCb]);

  return throttledCb;
};
